import { idDeEstaPagina } from '../session/pestanas';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly params?: Record<string, unknown>,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Único punto por el que el SPA habla con la API.
 *
 * La cookie de sesión la manda el navegador sola (`credentials: 'include'`); el
 * token anti-CSRF viaja en cabecera y sólo en escrituras. Los errores llegan como
 * códigos: aquí se convierten en `ApiError` y la interfaz los traduce.
 */
/**
 * Qué hacer cuando la API dice, en medio del uso, que la sesión ya no existe.
 * Lo registra `SessionContext`: sale por completo a la portada (ADR-0021).
 */
let alPerderSesion: ((codigo: string) => void) | null = null;
export function alPerderLaSesion(accion: ((codigo: string) => void) | null): void {
  alPerderSesion = accion;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  // FormData trae su propia frontera multipart: fijar content-type la rompe.
  const esFormData = init.body instanceof FormData;
  if (init.body && !esFormData && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (WRITE_METHODS.has(method) && csrfToken) {
    headers.set('x-csrf-token', csrfToken);
  }
  // Qué carga de la página pide (ADR-0030): una que volvió se distingue de una
  // petición rezagada de la que se cerró.
  headers.set('x-page-id', idDeEstaPagina());

  const response = await fetch(`/api/v1${path}`, {
    ...init,
    method,
    headers,
    credentials: 'include',
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const body = payload as { code?: string; params?: Record<string, unknown> } | null;
    // La primera consulta de sesión responde 401 a quien no ha entrado: eso no
    // es perderla. Cualquier otro 401 sí, y no se deja la pantalla a medias.
    if (response.status === 401 && path !== '/auth/session') {
      alPerderSesion?.(body?.code ?? 'auth.session_expired');
    }
    throw new ApiError(body?.code ?? 'internal.error', response.status, body?.params);
  }

  return payload as T;
}

/**
 * La última pestaña se va (ADR-0030). Con `keepalive` la petición sobrevive a la
 * página que se cierra —`sendBeacon` no puede llevar el token anti-CSRF—. No pasa
 * por `apiFetch`: un 401 a media salida no tiene a quién sacar del portal.
 */
export function avisarQueSeVa(): void {
  const headers: Record<string, string> = { 'x-page-id': idDeEstaPagina() };
  if (csrfToken) headers['x-csrf-token'] = csrfToken;
  void fetch('/api/v1/auth/session/leave', {
    method: 'POST',
    keepalive: true,
    credentials: 'include',
    headers,
  }).catch(() => undefined);
}

export interface EstadoDeSesion {
  /** Cuándo vence si nadie la usa. */
  expiresAt: string;
  /** Su tope: de ahí no pasa aunque se use. */
  absoluteExpiresAt: string;
  /** La hora del servidor, para no depender del reloj de la computadora. */
  now: string;
}

export interface Category {
  id: string;
  parentId: string | null;
  code: string;
  name: string;
  path: string;
  depth: number;
  /** Los de toda la rama: los suyos y los de sus subcategorías. */
  documentCount: number;
  /** Sólo los clasificados directamente en ella. */
  ownDocumentCount: number;
  /** Sólo llega al administrar la taxonomía; en el listado normal no hace falta. */
  isActive?: boolean;
  sortOrder?: number;
}

export interface DocumentReadingSummary {
  documentId: string;
  code: string;
  referenceCode?: string;
  title: string;
  readers: number;
  sessions: number;
  totalSeconds: number;
  lastReadAt: string | null;
}

export interface ReadingEntry {
  sessionId: string;
  userId: string;
  username: string;
  fullName: string;
  documentId: string;
  documentCode: string;
  documentReferenceCode?: string;
  documentTitle: string;
  versionNumber: number;
  versionLabel: string;
  startedAt: string;
  endedAt: string | null;
  activeSeconds: number;
  pagesViewed: number;
  ipAddress: string;
  closeReason: string | null;
}

export interface PendingReader {
  userId: string;
  username: string;
  fullName: string;
  departmentName: string | null;
}

export interface AuditEvent {
  id: string;
  occurredAt: string;
  actorUserId: string | null;
  actorUsername: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  targetCode: string | null;
  targetReferenceCode: string | null;
  /** El título del documento o el usuario de la persona: «sobre qué», en palabras. */
  targetLabel: string | null;
  outcome: 'SUCCESS' | 'DENIED' | 'ERROR';
  reason: string | null;
  ipAddress: string | null;
  previousValue: unknown;
  newValue: unknown;
  correlationId: string | null;
}

export interface SealVerification {
  sealDate: string;
  intact: boolean;
  chained: boolean;
  expectedRows: number;
  actualRows: number;
}

export interface Summary {
  activeUsers: number;
  pendingInvitations: number;
  groups: number;
  categories: number;
  departments: number;
  publishedDocuments: number;
  expiringSoon: number;
  expiredDocuments: number;
  documentsWithoutGroup: number;
  reading: {
    windowDays: number;
    sessions: number;
    readers: number;
    totalSeconds: number;
    documentsRead: number;
    mostRead: DocumentReadingSummary[];
  };
  expirationJob: {
    lastSuccessAt: string | null;
    delayed: boolean;
    toleratedHours: number;
  };
}

export interface DocumentSummary {
  id: string;
  code: string;
  /** El código con el que la caja conoce el documento. Texto libre y opcional. */
  referenceCode?: string;
  title: string;
  status: 'DRAFT' | 'PUBLISHED' | 'EXPIRED' | 'UNPUBLISHED' | 'ARCHIVED';
  categoryName: string;
  departmentName: string;
  issuedOn: string;
  expiresOn?: string;
  expiredAt?: string;
  versionNumber?: number;
  versionLabel?: string;
  languageCode: string;
  isPrintable: boolean;
  isDownloadable: boolean;
  printWatermark: boolean;
  downloadWatermark: boolean;
  /** Si su lectura es obligatoria y con cuántos días de plazo. */
  mandatoryReading: boolean;
  readingDeadlineDays?: number;
}

export interface DocumentDetail extends DocumentSummary {
  summary?: string;
  categoryId: string;
  /** Opcional: la caja no clasifica por área. */
  ownerDepartmentId?: string;
  groupIds: string[];
  keywords: string[];
  isPrintable: boolean;
  isDownloadable: boolean;
  /** Si la copia que sale del portal lleva el nombre de quien la obtuvo. */
  printWatermark: boolean;
  downloadWatermark: boolean;
  currentVersionId?: string;
}

export interface DocumentVersion {
  id: string;
  versionNumber: number;
  versionLabel: string;
  status: 'UPLOADED' | 'PUBLISHED' | 'SUPERSEDED' | 'REJECTED' | 'INTEGRITY_FAILED';
  avStatus: 'PENDING' | 'CLEAN' | 'INFECTED' | 'ERROR';
  sizeBytes: number;
  pageCount?: number;
  sha256: string;
  uploadedAt: string;
  publishedAt?: string;
  /** Cuándo dejó de ser la vigente: decide cuál vuelve si se elimina la que la reemplazó. */
  supersededAt?: string;
}

/** Lo que dejó la baja de una versión: cuál se fue y, si era la vigente, cuál volvió. */
export interface VersionDeletionResult {
  deletedVersion: string;
  deletedVersionSha256: string;
  restoredVersion?: string;
}

export interface CreateDocumentBody {
  /** La asigna el servidor. Se conserva por si un día entra un acervo con las suyas. */
  code?: string;
  /** Código de documento de la caja: texto libre, opcional. `null` lo borra al editar. */
  referenceCode?: string | null;
  title: string;
  summary?: string;
  categoryId: string;
  ownerDepartmentId?: string;
  issuedOn: string;
  expiresOn?: string;
  groupIds: string[];
  keywords?: string[];
  isPrintable: boolean;
  isDownloadable: boolean;
  printWatermark?: boolean;
  downloadWatermark?: boolean;
  mandatoryReading?: boolean;
  readingDeadlineDays?: number;
}

/** Lo que una persona tiene pendiente de leer. `daysLeft` negativo = vencido. */
export interface PendingReading {
  documentId: string;
  code: string;
  referenceCode?: string;
  title: string;
  versionId: string;
  versionLabel: string;
  since: string;
  dueOn: string;
  daysLeft: number;
}

export interface PendingReader {
  userId: string;
  username: string;
  fullName: string;
  email: string;
  dueOn: string;
  daysLeft: number;
}

/** Un integrante de la audiencia de un documento obligatorio, haya leído o no. */
export interface DocumentReader {
  userId: string;
  username: string;
  fullName: string;
  email: string;
  dueOn: string;
  daysLeft: number;
  acknowledgedAt?: string;
  lastRemindedOn?: string;
  /** Páginas de la versión vigente que ya pasaron por su pantalla, y de cuántas. */
  pagesRead: number;
  pageCount: number | null;
}

/** Gerencia: sus grupos, su gente y cómo va cada quien con lo obligatorio. */
export interface TeamReadings {
  groups: { id: string; name: string }[];
  members: { userId: string; username: string; fullName: string; email: string; groupIds: string[] }[];
  assignments: TeamAssignment[];
}

export interface TeamAssignment {
  userId: string;
  documentId: string;
  code: string;
  referenceCode?: string;
  title: string;
  versionLabel: string;
  deadlineDays: number;
  dueOn: string;
  daysLeft: number;
  acknowledgedAt?: string;
  lastRemindedOn?: string;
  pagesRead: number;
  pageCount: number | null;
}

/** Lo que muestra la pantalla del código por correo. */
export interface EmailCodeStatus {
  maskedEmail: string;
  expiresAt?: string;
  canResendAt?: string;
  sendsLeft: number;
  attemptsLeft: number;
  /** La hora del servidor, para medir lo que falta sin depender del reloj local. */
  now: string;
}

/** Los motivos de eliminación de un documento: códigos, la pantalla los traduce. */
export const MOTIVOS_DE_BAJA = [
  'UPLOADED_BY_MISTAKE',
  'DUPLICATE',
  'REPLACED_BY_ANOTHER',
  'NO_LONGER_APPLIES',
  'CONFIDENTIAL_INFORMATION',
  'OTHER',
] as const;

export interface MandatoryDocumentStatus {
  documentId: string;
  code: string;
  referenceCode?: string;
  title: string;
  versionLabel: string;
  deadlineDays: number;
  audience: number;
  acknowledged: number;
  overdue: number;
}

export interface InitialLoadPlan {
  errores: { hoja: string; fila?: number; mensaje: string }[];
  resumen: {
    usuariosNuevos: number;
    usuariosExistentes: number;
    gruposNuevos: number;
    gruposExistentes: number;
    membresiasNuevas: number;
    categoriasNuevas: number;
    documentosNuevos: number;
    documentosExistentes: number;
    documentosPendientes: number;
  };
  gruposNuevos: string[];
  categoriasNuevas: string[];
  cambiosDeRol: { correo: string; de: string; a: string }[];
  pendientes: { titulo: string; motivo: string }[];
  documentosExistentes: { titulo: string; codigo: string }[];
}

export interface InitialLoadResult extends InitialLoadPlan {
  aplicado: true;
  fallos: { que: string; detalle: string }[];
}

export const api = {
  session: () => apiFetch<SessionResponse>('/auth/session'),
  /** Si la sesión sigue viva. No cuenta como actividad: no la alarga. */
  sessionStatus: () => apiFetch<EstadoDeSesion>('/auth/session/status'),
  /** «Sigo aquí»: alarga la sesión como cualquier otra cosa que haga la persona. */
  extendSession: () => apiFetch<void>('/auth/session/extend', { method: 'POST' }),
  logout: () => apiFetch<void>('/auth/logout', { method: 'POST' }),
  groups: (includeInactive = false) =>
    apiFetch<Group[]>(`/groups${includeInactive ? '?includeInactive=true' : ''}`),
  // La clave no se envía: la deriva el servidor del nombre. Dejó de pedirse
  // porque no le servía a nadie y era un campo más que llenar mal.
  createGroup: (nombre: string, descripcion?: string, parentId?: string) =>
    apiFetch<Group>('/groups', {
      method: 'POST',
      body: JSON.stringify({
        fallbackName: nombre,
        translations: [{ locale: 'es-MX', name: nombre, description: descripcion }],
        ...(parentId ? { parentId } : {}),
      }),
    }),
  /** `parentId: null` lo vuelve un grupo principal; sin él, no se mueve. */
  updateGroup: (id: string, nombre: string, descripcion?: string, parentId?: string | null) =>
    apiFetch<Group>(`/groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        fallbackName: nombre,
        translations: [{ locale: 'es-MX', name: nombre, description: descripcion }],
        ...(parentId !== undefined ? { parentId } : {}),
      }),
    }),
  // Desactiva, no borra: un grupo que se usó para dar acceso a documentos tiene
  // que seguir existiendo para que la bitácora siga siendo legible.
  deactivateGroup: (id: string) => apiFetch<void>(`/groups/${id}`, { method: 'DELETE' }),
  groupMembers: (id: string) => apiFetch<GroupMember[]>(`/groups/${id}/members`),
  replaceGroupMembers: (id: string, userIds: string[]) =>
    apiFetch<GroupMember[]>(`/groups/${id}/members`, {
      method: 'PUT',
      body: JSON.stringify({ userIds }),
    }),
  categories: (includeInactive = false) =>
    apiFetch<Category[]>(`/categories${includeInactive ? '?includeInactive=true' : ''}`),
  createCategory: (body: { code: string; name: string; parentId?: string }) =>
    apiFetch<Category>('/categories', { method: 'POST', body: JSON.stringify(body) }),
  updateCategory: (
    id: string,
    patch: { name?: string; parentId?: string | null; isActive?: boolean },
  ) => apiFetch<Category>(`/categories/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  locales: () => apiFetch<{ code: string; name: string }[]>('/locales'),
  resendInvitation: (userId: string) =>
    apiFetch<void>(`/users/${userId}/invitation`, { method: 'POST' }),

  /** Reenvía la invitación a todas las cuentas que siguen esperándola. */
  resendAllInvitations: () =>
    apiFetch<{ scheduled: number }>('/users/invitations/resend-all', { method: 'POST' }),

  /** Manda el correo para que la persona defina una contraseña nueva. */
  sendPasswordReset: (userId: string) =>
    apiFetch<void>(`/users/${userId}/password-reset`, { method: 'POST' }),
  // Deshabilitar alcanza también a Keycloak, y el permiso se evalúa contra el
  // estado actual en cada petición: quien queda suspendido deja de poder actuar
  // de inmediato, no cuando expire su sesión.
  setUserStatus: (userId: string, status: 'ACTIVE' | 'SUSPENDED' | 'TERMINATED') =>
    apiFetch<unknown>(`/users/${userId}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  emailDeliveries: () => apiFetch<EmailDelivery[]>('/users/email-deliveries'),
  exportUsersUrl: (query = '') => `/api/v1/users/export${query}`,
  /** Quitar el bloqueo de quien se equivocó tres veces de contraseña. */
  unlockUser: (userId: string) =>
    apiFetch<{ done: true }>(`/users/${userId}/unlock`, { method: 'POST' }),
  /** Dispositivos autorizados (ADR-0026): la bandeja de quien aprueba. */
  devices: (status?: DeviceStatus) =>
    apiFetch<Device[]>(`/devices${status ? `?status=${status}` : ''}`),
  /** Cuántas esperan y la más reciente. Es consulta de fondo: no alarga la sesión. */
  devicePendingCount: () => apiFetch<DevicePendingSummary>('/devices/pending-count'),
  decideDevice: (deviceId: string, decision: 'approve' | 'reject' | 'revoke') =>
    apiFetch<DeviceDecision>(`/devices/${deviceId}/${decision}`, { method: 'POST' }),
  /** Los grupos y su gente, en Excel. */
  exportGroupsUrl: () => '/api/v1/groups/export',
  /** Gerencia: el equipo de quien pregunta, nunca el de otro. */
  teamReadings: () => apiFetch<TeamReadings>('/team/readings'),
  teamRemind: (body: { documentId?: string; userIds?: string[] }) =>
    apiFetch<{ sent: number; failed: number }>('/team/reminders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  teamExportUrl: () => '/api/v1/team/readings/export',
  /** Código por correo, para quien administra y no trae su teléfono. Sin sesión todavía. */
  emailCodeStatus: () => apiFetch<EmailCodeStatus>('/auth/second-factor/email/status'),
  emailCodeSend: () =>
    apiFetch<EmailCodeStatus>('/auth/second-factor/email/send', { method: 'POST' }),
  emailCodeVerify: (code: string) =>
    apiFetch<{ returnTo: string }>('/auth/second-factor/email/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  profile: () => apiFetch<Profile>('/profile'),
  updateProfile: (preferredLocale: string) =>
    apiFetch<Profile>('/profile', {
      method: 'PATCH',
      body: JSON.stringify({ preferredLocale }),
    }),
  /** Qué haría la carga inicial con este libro. No escribe nada. */
  previewInitialLoad: (archivo: File) => {
    const datos = new FormData();
    datos.append('file', archivo);
    return apiFetch<InitialLoadPlan>('/initial-load/preview', { method: 'POST', body: datos });
  },
  applyInitialLoad: (archivo: File, enviarInvitaciones: boolean) => {
    const datos = new FormData();
    datos.append('file', archivo);
    datos.append('enviarInvitaciones', String(enviarInvitaciones));
    return apiFetch<InitialLoadResult>('/initial-load/apply', { method: 'POST', body: datos });
  },
  /** La foto de alguien. La asignan el administrador general y el local (`user.photo`). */
  setUserPhoto: (userId: string, archivo: File) => {
    const datos = new FormData();
    datos.append('file', archivo);
    return apiFetch<{ photoUpdatedAt: string }>(`/users/${userId}/photo`, { method: 'PUT', body: datos });
  },
  removeUserPhoto: (userId: string) =>
    apiFetch<{ photoUpdatedAt: null }>(`/users/${userId}/photo`, { method: 'DELETE' }),
  resetMySecondFactor: () =>
    apiFetch<{ done: true }>('/profile/second-factor/reset', { method: 'POST' }),
  resetSecondFactorOf: (userId: string) =>
    apiFetch<{ done: true }>(`/users/${userId}/second-factor/reset`, { method: 'POST' }),
  summary: () => apiFetch<Summary>('/dashboard/summary'),
  audit: (query = '') => apiFetch<Paged<AuditEvent>>(`/audit${query ? `?${query}` : ''}`),
  auditActions: () => apiFetch<string[]>('/audit/actions'),
  auditSeals: () =>
    apiFetch<{ items: SealVerification[]; allIntact: boolean; checked: number }>('/audit/seals'),
  accessLog: (query = '') =>
    apiFetch<Paged<ReadingEntry>>(`/access-log/by-user${query ? `?${query}` : ''}`),
  accessTimeline: (query = '') =>
    apiFetch<{ day: string; sessions: number; readers: number; totalSeconds: number }[]>(
      `/access-log/timeline${query ? `?${query}` : ''}`,
    ),
  accessLogByDocument: (query = '') =>
    apiFetch<DocumentReadingSummary[]>(`/access-log/by-document${query ? `?${query}` : ''}`),
  /** Uso de un documento con quién no lo ha abierto en la misma respuesta. */
  documentAccessReport: (documentId: string, query = '') =>
    apiFetch<{
      summary: DocumentReadingSummary | null;
      readers: ReadingEntry[];
      neverOpened: PendingReader[];
    }>(`/access-log/by-document/${documentId}${query ? `?${query}` : ''}`),
  documents: (query = '') => apiFetch<Paged<DocumentSummary>>(`/documents${query}`),
  document: (id: string) => apiFetch<DocumentDetail>(`/documents/${id}`),
  // Devuelve una URL firmada que caduca. La descarga queda en la bitácora del
  // lado del servidor: por eso es POST y no un enlace directo al archivo — un
  // enlace que se puede reenviar sería una descarga que nadie registró.
  downloadLink: (id: string, versionId?: string) =>
    apiFetch<{
      url: string;
      expiresAt: string;
      fileName: string;
      /** Sólo viene con el original. La copia marcada es otro archivo. */
      sha256?: string;
      /** Con marca la liga apunta a la propia API, no al almacén. */
      watermarked: boolean;
    }>(
      `/documents/${id}/download`,
      { method: 'POST', body: JSON.stringify(versionId ? { versionId } : {}) },
    ),
  /** Lo que yo tengo pendiente. No admite preguntar por otra persona. */
  pendingReadings: () => apiFetch<PendingReading[]>('/reading/pending'),
  mandatoryOverview: () => apiFetch<MandatoryDocumentStatus[]>('/reading/overview'),
  pendingReadersOf: (documentId: string) =>
    apiFetch<PendingReader[]>(`/reading/overview/${documentId}`),
  /** Toda la audiencia del documento: quién acusó, quién no y su último recordatorio. */
  documentReaders: (documentId: string) =>
    apiFetch<DocumentReader[]>(`/documents/${documentId}/readers`),
  /** Recordatorio a mano. Sin lista, va a todos los que faltan. */
  sendReadingReminders: (documentId: string, userIds?: string[]) =>
    apiFetch<{ sent: number; failed: number }>(`/documents/${documentId}/reminders`, {
      method: 'POST',
      body: JSON.stringify(userIds ? { userIds } : {}),
    }),
  /** El acuse de lectura. Queda con fecha, dirección y tiempo acreditado. */
  acknowledge: (documentId: string, versionId?: string, activeSeconds?: number) =>
    apiFetch<{ acknowledged: true; new: boolean }>(`/documents/${documentId}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify({ versionId, activeSeconds }),
    }),
  updateDocument: (id: string, patch: Partial<CreateDocumentBody>) =>
    apiFetch<DocumentDetail>(`/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  /**
   * La audiencia va por su propio camino, no dentro del PATCH de la ficha.
   *
   * Es la misma separación que hace el servidor: cambiar quién ve un documento
   * no es corregirle el título, y en la bitácora tiene que poder distinguirse
   * una cosa de la otra sin leer el cuerpo de la petición.
   */
  updateDocumentGroups: (id: string, groupIds: string[]) =>
    apiFetch<DocumentDetail>(`/documents/${id}/groups`, {
      method: 'PUT',
      body: JSON.stringify({ groupIds }),
    }),
  createDocument: (body: CreateDocumentBody) =>
    apiFetch<DocumentDetail>('/documents', { method: 'POST', body: JSON.stringify(body) }),
  /** Eliminar, con motivo. Sólo la cuenta original del sistema. */
  deleteDocument: (id: string, reason: string, detail?: string) =>
    apiFetch<void>(`/documents/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(detail ? { reason, detail } : { reason }),
    }),
  documentVersions: (id: string) => apiFetch<DocumentVersion[]>(`/documents/${id}/versions`),
  /** Baja de una versión, con motivo. Si era la vigente, vuelve a regir la de antes. */
  deleteVersion: (documentId: string, versionId: string, reason: string, detail?: string) =>
    apiFetch<VersionDeletionResult>(`/documents/${documentId}/versions/${versionId}`, {
      method: 'DELETE',
      body: JSON.stringify(detail ? { reason, detail } : { reason }),
    }),
  /**
   * Publica una versión. En un documento de lectura obligatoria, `notifyNow`
   * avisa en ese momento a quienes deben leerlo; si no, sale a las 8:00.
   */
  publishVersion: (id: string, versionId: string, notifyNow = false) =>
    apiFetch<DocumentDetail & { notified?: { sent: number; failed: number; error?: string } }>(
      `/documents/${id}/publish`,
      { method: 'POST', body: JSON.stringify({ versionId, notifyNow }) },
    ),
  /** Corrige la etiqueta de una versión ya cargada. */
  renameVersion: (documentId: string, versionId: string, versionLabel: string) =>
    apiFetch<DocumentVersion>(`/documents/${documentId}/versions/${versionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ versionLabel }),
    }),
  uploadVersion: (id: string, archivo: File) => {
    const datos = new FormData();
    datos.append('file', archivo);
    // Sin content-type explicito: el navegador arma el multipart con su frontera.
    return apiFetch<DocumentVersion>(`/documents/${id}/versions`, {
      method: 'POST',
      body: datos,
    });
  },
  /**
   * Carga con progreso real. Es el único sitio donde no se usa `fetch`.
   *
   * `fetch` no informa de cuánto lleva subido —su cuerpo no es observable— así
   * que un archivo de 20 MB por VPN dejaba la pantalla quieta sin decir nada, y
   * lo quieto se confunde con lo roto. `XMLHttpRequest` sí publica
   * `upload.onprogress`, y por eso sobrevive aquí. Todo lo demás —cookie de
   * sesión, token anti-CSRF, códigos de error— se replica igual que en
   * `apiFetch`; si aquello cambia, esto tiene que cambiar con ello.
   */
  uploadVersionConProgreso: (
    id: string,
    archivo: File,
    alAvanzar: (porcentaje: number) => void,
  ): Promise<DocumentVersion> =>
    new Promise((resolver, rechazar) => {
      const peticion = new XMLHttpRequest();
      peticion.open('POST', `/api/v1/documents/${id}/versions`);
      peticion.withCredentials = true;
      if (csrfToken) peticion.setRequestHeader('x-csrf-token', csrfToken);
      peticion.setRequestHeader('x-page-id', idDeEstaPagina());

      peticion.upload.addEventListener('progress', (evento) => {
        // Sin `lengthComputable` no hay porcentaje que dar: mejor no inventarlo.
        if (evento.lengthComputable) alAvanzar(Math.round((evento.loaded / evento.total) * 100));
      });

      peticion.addEventListener('load', () => {
        const cuerpo = ((): { code?: string; params?: Record<string, unknown> } | null => {
          try {
            return JSON.parse(peticion.responseText) as { code?: string };
          } catch {
            return null;
          }
        })();
        if (peticion.status >= 200 && peticion.status < 300) {
          resolver(cuerpo as unknown as DocumentVersion);
        } else {
          rechazar(new ApiError(cuerpo?.code ?? 'internal.error', peticion.status, cuerpo?.params));
        }
      });

      // Una red que se cae no es un 500: decirlo como tal manda a Sistemas a
      // revisar un servidor que está bien.
      peticion.addEventListener('error', () => rechazar(new ApiError('network.error', 0)));
      peticion.addEventListener('abort', () => rechazar(new ApiError('upload.cancelled', 0)));

      const datos = new FormData();
      datos.append('file', archivo);
      peticion.send(datos);
    }),

  departments: () => apiFetch<NamedRef[]>('/departments'),
  roles: () => apiFetch<Role[]>('/roles'),
  /** Los permisos con su nombre en el idioma de la persona, para enseñarlos en palabras. */
  permissions: () => apiFetch<{ code: string; name: string }[]>('/permissions'),
  /** Reemplaza el rol de una persona. No se puede otorgar más de lo que uno tiene. */
  replaceUserRoles: (userId: string, roleId: string) =>
    apiFetch<void>(`/users/${userId}/roles`, {
      method: 'PUT',
      body: JSON.stringify({ assignments: [{ roleId, scopeType: 'GLOBAL' }] }),
    }),
  usersSummary: () =>
    apiFetch<{ total: number; byStatus: Record<string, number> }>('/users/summary'),
  users: (query = '') => apiFetch<Paged<User>>(`/users${query}`),
  invite: (body: InviteBody) => apiFetch<User>('/users', { method: 'POST', body: JSON.stringify(body) }),

  openView: (documentId: string, versionId?: string) =>
    apiFetch<OpenedView>(`/documents/${documentId}/view-grant`, {
      method: 'POST',
      body: JSON.stringify(versionId ? { versionId } : {}),
    }),
  renewGrant: (grantId: string) =>
    apiFetch<{ jti: string; expiresAt: string }>(`/viewer/${grantId}/renew`, { method: 'POST' }),
  /**
   * La pagina no pasa por `apiFetch`: la respuesta es una imagen, no JSON. Se
   * pide como blob y se libera al cambiar de pagina — nunca se guarda.
   *
   * `alAvanzar` recibe cuánto lleva de 0 a 1 mientras baja, o `null` mientras el
   * servidor todavía la prepara (rasterizar y marcar la primera vez tarda): con
   * eso el visor enseña una barra que se mueve en vez de una pantalla en blanco.
   */
  viewerPage: async (
    grantId: string,
    pageNumber: number,
    zoom: number,
    alAvanzar?: (avance: number | null) => void,
    senal?: AbortSignal,
  ): Promise<Blob> => {
    alAvanzar?.(null);
    const response = await fetch(`/api/v1/viewer/${grantId}/pages/${pageNumber}?zoom=${zoom}`, {
      credentials: 'include',
      signal: senal,
    });
    if (!response.ok) {
      const cuerpo = (await response.json().catch(() => null)) as { code?: string } | null;
      throw new ApiError(cuerpo?.code ?? 'internal.error', response.status);
    }
    const total = Number(response.headers.get('content-length') ?? 0);
    if (!alAvanzar || !response.body || !total) return response.blob();

    const lector = response.body.getReader();
    const pedazos: BlobPart[] = [];
    let recibidos = 0;
    for (;;) {
      const { done, value } = await lector.read();
      if (done) break;
      pedazos.push(value as Uint8Array<ArrayBuffer>);
      recibidos += value.length;
      alAvanzar(Math.min(1, recibidos / total));
    }
    return new Blob(pedazos, { type: response.headers.get('content-type') ?? 'image/webp' });
  },
  /**
   * En qué página está y, si ya estuvo el tiempo suficiente a la vista, que se
   * leyó. Lo manda el visor solo: no alarga la sesión.
   */
  /** Un posible intento de captura de pantalla: queda en la bitácora (ADR-0029). */
  reportCapture: (grantId: string, page: number, signal: 'print_screen' | 'shortcut') =>
    apiFetch<unknown>(`/viewer/${grantId}/capture`, {
      method: 'POST',
      body: JSON.stringify({ page, signal }),
    }),
  markPage: (grantId: string, page: number, read: boolean) =>
    apiFetch<{ pagesRead: number; pageCount: number; complete: boolean }>(
      `/viewer/${grantId}/progress`,
      { method: 'POST', body: JSON.stringify({ page, read }) },
    ),
  /**
   * La sesión de lectura no se nombra desde aquí: el servidor la deduce de la
   * concesión, que ya sabe quién es y qué está viendo.
   */
  /**
   * La copia marcada: se trae como blob porque un error tiene que poder decirse.
   *
   * Con un ancla apuntando directo a la liga, un 403 o un fallo a medio armar
   * llegan como una descarga que el navegador cancela en silencio, y la pantalla
   * se queda tan contenta. Así el código de error vuelve por el mismo camino que
   * el resto y sale como aviso.
   *
   * Devuelve una URL de objeto: quien la use la libera.
   */
  descargarArchivo: async (ruta: string): Promise<string> => {
    const response = await fetch(ruta, { credentials: 'include' });
    if (!response.ok) {
      const cuerpo = (await response.json().catch(() => null)) as { code?: string } | null;
      throw new ApiError(cuerpo?.code ?? 'internal.error', response.status);
    }
    return URL.createObjectURL(await response.blob());
  },
  /**
   * El imprimible lo arma el servidor, no el navegador: así el permiso se
   * verifica donde se verifica todo, y la impresión queda en la bitácora.
   */
  printablePdf: async (grantId: string): Promise<Blob> => {
    const response = await fetch(`/api/v1/viewer/${grantId}/printable`, {
      method: 'POST',
      credentials: 'include',
      headers: csrfToken ? { 'x-csrf-token': csrfToken } : undefined,
    });
    if (!response.ok) {
      const cuerpo = (await response.json().catch(() => null)) as { code?: string } | null;
      throw new ApiError(cuerpo?.code ?? 'internal.error', response.status);
    }
    return response.blob();
  },
  heartbeat: (grantId: string, seconds: number, pageSeen: boolean) =>
    apiFetch<{ creditedSeconds: number }>(`/viewer/${grantId}/heartbeat`, {
      method: 'POST',
      body: JSON.stringify({ seconds, pageSeen }),
    }),
  closeView: (grantId: string, reason = 'CLOSED', seconds = 0) =>
    apiFetch<{ closed: true }>(`/viewer/${grantId}/close`, {
      method: 'POST',
      body: JSON.stringify({ reason, seconds }),
    }),
};

export interface OpenedView {
  jti: string;
  expiresAt: string;
  documentId: string;
  versionId: string;
  versionNumber: number;
  versionLabel: string;
  isCurrentVersion: boolean;
  isExpiredDocument: boolean;
  title: string;
  code: string;
  referenceCode?: string;
  pageCount: number;
  canPrint: boolean;
  canDownload: boolean;
  /** Lectura obligatoria, y si esta persona ya acusó **esta** versión. */
  mandatoryReading: boolean;
  acknowledged: boolean;
  heartbeatSeconds: number;
  /** Dónde se quedó la última vez, y qué páginas de esta versión ya leyó. */
  lastPage: number;
  pagesRead: number[];
  /**
   * Cuánto tiene que estar cada página a la vista para contar como leída, en
   * orden: según su texto (ADR-0028). El servidor lo vuelve a comprobar.
   */
  pageSeconds: number[];
}

export interface NamedRef {
  id: string;
  code: string;
  name: string;
}

export interface GroupMember {
  userId: string;
  addedAt: string;
  addedBy?: string;
  /** El servidor resuelve la persona; puede venir nula si el espejo se borró. */
  user: { id: string; username: string; fullName: string } | null;
}

export interface EmailDelivery {
  id: string;
  username: string;
  email: string;
  kind: 'INVITATION' | 'RESEND' | 'PASSWORD_RESET';
  attemptedAt: string;
  /** SENT = el servidor de correo aceptó el mensaje, no que haya llegado. */
  outcome: 'SENT' | 'FAILED';
  detail?: string;
  requestedByUsername?: string;
}

export interface Profile {
  id: string;
  username: string;
  fullName: string;
  email: string;
  preferredLocale: string | null;
  lastLoginAt: string | null;
  /** Marca de tiempo, no la imagen: va en la URL para que el navegador la cachee. */
  photoUpdatedAt: string | null;
  groups: NamedRef[];
  roles: string[];
}

/**
 * Dirección de la foto de alguien. Lleva la marca de tiempo a propósito: la
 * respuesta se cachea un año, así que una foto nueva tiene que ser otra URL o
 * el navegador seguiría mostrando la vieja.
 */
export function urlDeFoto(userId: string, actualizada: string | null): string | null {
  return actualizada ? `/api/v1/profile/photo/${userId}?v=${Date.parse(actualizada)}` : null;
}

export interface Group extends NamedRef {
  /** El grupo general: no se puede desactivar. */
  isSystem?: boolean;
  /** Integrantes directos. */
  memberCount: number;
  /** Personas en toda la rama: quienes verían lo que se le asigne (ADR-0027). */
  branchMemberCount: number;
  /** De quién cuelga; sin él, es un grupo principal. */
  parentId?: string;
  isActive: boolean;
}

export interface Role extends NamedRef {
  isPrivileged: boolean;
  permissions: string[];
}

export interface SessionResponse {
  user: {
    id: string;
    username: string;
    departmentId?: string;
    branchId?: string;
    status: string;
  };
  permissions: string[];
  groups: Group[];
  locale: string;
  mfaVerified: boolean;
  /**
   * Si a esta persona le hace falta segundo factor por lo que puede hacer. Lo
   * decide el servidor: quien sólo consulta nunca lo necesita, y no tiene por
   * qué ver que le falta algo.
   */
  mfaRequired: boolean;
  /** Si administra el portal: lo que decide qué se le enseña, como sus permisos. */
  administers: boolean;
  /** Si es la cuenta original del sistema: la única que puede eliminar documentos. */
  canDeleteDocuments: boolean;
  csrfToken: string;
  expiresAt: string;
  photoUpdatedAt: string | null;
}

export interface User {
  id: string;
  username: string;
  fullName: string;
  email: string;
  departmentId?: string;
  status: 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'TERMINATED';
  groupIds: string[];
  /** Códigos de rol, p. ej. `READER`. Vacío significa que no tiene ninguno. */
  roleCodes: string[];
  lastLoginAt?: string;
  /** Se equivocó tres veces de contraseña: no entra hasta que un administrador lo desbloquee. */
  locked?: boolean;
  /** Cuándo se le puso la foto; sin ella, no tiene. Ver `urlDeFoto`. */
  photoUpdatedAt?: string;
}

export type DeviceKind = 'PHONE' | 'TABLET' | 'COMPUTER' | 'UNKNOWN';
export type DeviceStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'REVOKED' | 'EXEMPT';

/** Un dispositivo desde el que alguien entra o pidió entrar (ADR-0026). */
export interface Device {
  id: string;
  userId: string;
  username: string;
  fullName: string;
  kind: DeviceKind;
  browser: string;
  os: string;
  status: DeviceStatus;
  requestedAt: string;
  firstIp?: string;
  lastIp?: string;
  lastSeenAt?: string;
  decidedAt?: string;
  decidedByUsername?: string;
}

export interface DevicePendingSummary {
  pending: number;
  latest?: Pick<Device, 'id' | 'fullName' | 'kind' | 'browser' | 'os' | 'requestedAt'>;
}

export interface DeviceDecision {
  id: string;
  status: DeviceStatus;
  person: string;
  device: string;
  /** Si el correo a la persona salió. Si no, hay que avisarle por otro lado. */
  notified: boolean;
}

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface InviteBody {
  fullName: string;
  email: string;
  username: string;
  departmentId?: string;
  roles: { roleId: string; scopeType: 'GLOBAL' | 'DEPARTMENT' | 'BRANCH'; scopeId?: string }[];
  groupIds?: string[];
}
